import { useEffect, useMemo, useState } from "react";

import CustomGrid from "../CustomGrid";
import { gridStructure } from "constants/CustomGridStructure";
import { GridRowSelectionModel } from "@mui/x-data-grid";

import { Box, Paper } from "@mui/material";
import Loader from "../Loader";

import { EnhancedTableToolbar } from "../EnhancedToolbar";

import { Location, SubLocation } from "types";
import { LOCATIONS_URL as BASE_URL } from "constants/urls";
import { ENTITY } from "constants/entities";

import useCrud from "../../../hooks/useCrud";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import { useFilter } from "hooks/useFilter";
import { getUserFullName } from "helpers";
import { exportToExcel } from "helpers/exportToExcel";

type SubLocationExcelRow = SubLocation & {
  store_code?: string;
  parent_code?: string;
  created_by_name?: string;
};

export default function SublocationsList() {
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);

  const {
    sublocations,
    users,
  } = useSession();
  const { showSnackBar } = useToast();

  const {
    loading,
    firstLoad,
    showToast,
    toastMessage,
    toastSeverity,
    setShowToast,
  } = useCrud<Location>(BASE_URL);

  const locationsExcelColumns = {
    "Código": (l: SubLocationExcelRow) => l.code ?? "-",

    "Ubicación Padre": (l: SubLocationExcelRow) =>
      l.parent_code ?? l.parent?.code ?? "-",

    "Almacén": (l: SubLocationExcelRow) =>
      l.store_code ?? l.store?.code ?? "-",

    "Tipo": (l: SubLocationExcelRow) => l.location_type ?? "-",

    "Ancho": (l: SubLocationExcelRow) => l.width ?? 0,

    "Alto": (l: SubLocationExcelRow) => l.height ?? 0,

    "Largo": (l: SubLocationExcelRow) => l.long ?? 0,

    "Creado por": (l: SubLocationExcelRow) => l.created_by_name ?? "-",
  } satisfies Record<string, (row: SubLocationExcelRow) => any>;

  const enrichedSubLocations = useMemo(() => {
    return sublocations.map((item) => ({
      ...item,
      store_code: item.store.code,
      parent_code: item.parent?.code ?? "-",
      created_by_name: (item as SubLocation & { created_by?: number | null })
        .created_by
        ? getUserFullName(
            (item as SubLocation & { created_by?: number | null }).created_by,
            users,
          )
        : "SISTEMA",
    }));
  }, [sublocations, users]);

  const { filterText, setFilterText, filteredData: filteredLocations } =
    useFilter<
      SubLocation & {
        store_code: string;
        parent_code: string;
        created_by_name: string;
      }
    >({
      data: enrichedSubLocations,
      fields: gridStructure[ENTITY.SUBLOCATIONS].fields,
    });

  const handleExportLocationsExcel = () => {
    exportToExcel<SubLocationExcelRow>({
      data: filteredLocations,
      columns: locationsExcelColumns,
      sheetName: "Sub-ubicaciones",
      fileNamePrefix: "Sub-ubicaciones",
      headerRows: [
        ["Listado de Sub-ubicaciones"],
        [`Filtro: ${filterText?.trim() ? filterText : "—"}`],
        [],
      ],
    });
  };

  useEffect(() => {
    if (showToast) {
      showSnackBar(toastMessage, toastSeverity);
      setShowToast(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  return (
    <>
      {firstLoad ? (
        <Loader isFirstLoad={firstLoad} isLoading={loading} />
      ) : (
        <>
          <Box sx={{ width: "99%" }}>
            <Paper sx={{ width: "100%", mb: 2 }}>
              <EnhancedTableToolbar
                numSelected={selected.length}
                onFilterChange={setFilterText}
                onExportExcel={handleExportLocationsExcel}
              />
              <CustomGrid
                structure={gridStructure[ENTITY.SUBLOCATIONS]}
                rows={filteredLocations}
                selectionModel={selected}
                onRowSelection={setSelected}
              />
            </Paper>
          </Box>
        </>
      )}
    </>
  );
}
